import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense } from "react";
import { Loader2 } from "lucide-react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { useVersionCheck } from "./hooks/useVersionCheck";

// Eagerly load critical routes
import Index from "./pages/Landing";
import Auth from "./pages/Auth";
import Layout from "./components/Layout";
import NotFound from "./pages/NotFound";
import { WorkspaceGuard } from "./components/WorkspaceGuard";

// Lazy load all other routes with retry logic for chunk loading errors
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const ContentStrategy = lazyWithRetry(() => import("./pages/ContentStrategy"));
const PostGenerator = lazyWithRetry(() => import("./pages/PostGenerator"));
const Engagement = lazyWithRetry(() => import("./pages/Engagement"));
const KnowledgeBase = lazyWithRetry(() => import("./pages/KnowledgeBase"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const AIUsage = lazyWithRetry(() => import("./pages/AIUsage"));
const Workspaces = lazyWithRetry(() => import("./pages/Workspaces"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const IndustryTrends = lazyWithRetry(() => import("./pages/IndustryTrends"));
const ViralContent = lazyWithRetry(() => import("./pages/ViralContent"));
const AdminLayout = lazyWithRetry(() => import("./components/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const Users = lazyWithRetry(() => import("./pages/admin/Users"));
const Wallets = lazyWithRetry(() => import("./pages/admin/Wallets"));
const UserActivity = lazyWithRetry(() => import("./pages/admin/UserActivity"));
const UserEdit = lazyWithRetry(() => import("./pages/admin/UserEdit"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const AIProviders = lazyWithRetry(() => import("./pages/admin/AIProviders"));
const AIPrompts = lazyWithRetry(() => import("./pages/admin/AIPrompts"));
const BlogManagement = lazyWithRetry(() => import("./pages/admin/BlogManagement"));
const BlogEditor = lazyWithRetry(() => import("./pages/admin/BlogEditor"));
const TrendsManagement = lazyWithRetry(() => import("./pages/admin/TrendsManagement"));
const AdminWorkspaces = lazyWithRetry(() => import("./pages/admin/AdminWorkspaces"));
const WorkspaceDetail = lazyWithRetry(() => import("./pages/admin/WorkspaceDetail"));
const ContentSearch = lazyWithRetry(() => import("./pages/admin/ContentSearch"));

// Agency Pages
const AgencyGuard = lazyWithRetry(() => import("./components/AgencyGuard").then(m => ({ default: m.AgencyGuard })));
const AgencyErrorBoundary = lazyWithRetry(() => import("./components/AgencyErrorBoundary").then(m => ({ default: m.AgencyErrorBoundary })));
const AgencyDashboard = lazyWithRetry(() => import("./pages/agency/AgencyDashboard"));
const AgencyAIUsage = lazyWithRetry(() => import("./pages/agency/AgencyAIUsage"));
const AgencyWallet = lazyWithRetry(() => import("./pages/agency/AgencyWallet"));
const AgencySettings = lazyWithRetry(() => import("./pages/agency/AgencySettings"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Optimized React Query configuration for better caching and performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache garbage collection after 10 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Retry failed requests once
    },
  },
});

const App = () => {
  // Check for new versions periodically
  useVersionCheck();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            
            {/* Workspace-protected routes - require workspace for agencies */}
            <Route path="/dashboard" element={<Layout><WorkspaceGuard><Dashboard /></WorkspaceGuard></Layout>} />
            <Route path="/knowledge" element={<Layout><WorkspaceGuard><KnowledgeBase /></WorkspaceGuard></Layout>} />
            <Route path="/strategy" element={<Layout><WorkspaceGuard><ContentStrategy /></WorkspaceGuard></Layout>} />
            <Route path="/posts" element={<Layout><WorkspaceGuard><PostGenerator /></WorkspaceGuard></Layout>} />
            <Route path="/engagement" element={<Layout><WorkspaceGuard><Engagement /></WorkspaceGuard></Layout>} />
            <Route path="/trends" element={<Layout><WorkspaceGuard><IndustryTrends /></WorkspaceGuard></Layout>} />
            <Route path="/industry-trends" element={<Layout><WorkspaceGuard><IndustryTrends /></WorkspaceGuard></Layout>} />
            <Route path="/viral-content" element={<Layout><WorkspaceGuard><ViralContent /></WorkspaceGuard></Layout>} />
            
            {/* Base routes - always accessible */}
            <Route path="/ai-usage" element={<Layout><AIUsage /></Layout>} />
            <Route path="/workspaces" element={<Layout><Workspaces /></Layout>} />
            <Route path="/settings" element={<Layout><Settings /></Layout>} />
            
            {/* Admin Routes */}
            <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
            <Route path="/admin/users" element={<AdminLayout><Users /></AdminLayout>} />
            <Route path="/admin/wallets" element={<AdminLayout><Wallets /></AdminLayout>} />
            <Route path="/admin/workspaces" element={<AdminLayout><AdminWorkspaces /></AdminLayout>} />
            <Route path="/admin/workspaces/:workspaceId" element={<AdminLayout><WorkspaceDetail /></AdminLayout>} />
            <Route path="/admin/users/:userId" element={<AdminLayout><UserActivity /></AdminLayout>} />
            <Route path="/admin/users/:userId/edit" element={<AdminLayout><UserEdit /></AdminLayout>} />
            <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
            <Route path="/admin/ai-providers" element={<AdminLayout><AIProviders /></AdminLayout>} />
            <Route path="/admin/ai-prompts" element={<AdminLayout><AIPrompts /></AdminLayout>} />
            <Route path="/admin/blog" element={<AdminLayout><BlogManagement /></AdminLayout>} />
            <Route path="/admin/blog/new" element={<AdminLayout><BlogEditor /></AdminLayout>} />
            <Route path="/admin/blog/edit/:id" element={<AdminLayout><BlogEditor /></AdminLayout>} />
            <Route path="/admin/trends" element={<AdminLayout><TrendsManagement /></AdminLayout>} />
            <Route path="/admin/content" element={<AdminLayout><ContentSearch /></AdminLayout>} />
            
            {/* Agency Routes - Protected by AgencyGuard and AgencyErrorBoundary */}
            <Route path="/agency/dashboard" element={<Layout><AgencyErrorBoundary><AgencyGuard><AgencyDashboard /></AgencyGuard></AgencyErrorBoundary></Layout>} />
            <Route path="/agency/ai-usage" element={<Layout><AgencyErrorBoundary><AgencyGuard><AgencyAIUsage /></AgencyGuard></AgencyErrorBoundary></Layout>} />
            <Route path="/agency/wallet" element={<Layout><AgencyErrorBoundary><AgencyGuard><AgencyWallet /></AgencyGuard></AgencyErrorBoundary></Layout>} />
            <Route path="/agency/workspaces" element={<Layout><AgencyErrorBoundary><AgencyGuard><Workspaces /></AgencyGuard></AgencyErrorBoundary></Layout>} />
            <Route path="/agency/settings" element={<Layout><AgencyErrorBoundary><AgencyGuard><AgencySettings /></AgencyGuard></AgencyErrorBoundary></Layout>} />
            
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
